/**
 * The mark, rasterised to pixels.
 *
 * Its own module because of where it has to run. A worker cannot decode an SVG
 * — `createImageBitmap` does not accept one in any engine that matters — so on
 * the worker path the shell calls this on the main thread and sends the pixels
 * across. The engine still calls it directly when it is running in-thread.
 */
import { MARK_SVG } from './mark.js';
import { surface, decodeSvg } from './env.js';

/**
 * Rasterises the mark and returns its coverage bitmap.
 *
 * The destination rectangle is worked out here rather than left to
 * `preserveAspectRatio`: an SVG carrying only a viewBox has an intrinsic size of
 * 300×150, which `drawImage` then stretches to whatever box it is given — so the
 * mark came out square and the cloud with it. Giving the root explicit
 * dimensions and fitting the rect by hand makes the shape exact.
 */
export function rasterise(svgText, size) {
  return new Promise((resolve, reject) => {
    const vb = svgText.match(/viewBox\s*=\s*"([^"]+)"/i);
    const nums = vb ? vb[1].trim().split(/[\s,]+/).map(Number) : null;
    const vw = nums && nums.length === 4 && nums[2] > 0 ? nums[2] : 1;
    const vh = nums && nums.length === 4 && nums[3] > 0 ? nums[3] : 1;
    const ratio = vw / vh;

    const cv = surface(size, size);
    const ctx = cv.getContext('2d', { willReadFrequently: true });

    const painted = svgText.replace(
      /<svg /i,
      `<svg width="${vw}" height="${vh}" preserveAspectRatio="xMidYMid meet" fill="#ffffff" `
    );

    decodeSvg(painted, vw, vh).then((img) => {
      // Fit the mark's own ratio inside the square, with a little air.
      const inner = size * 0.9;
      let dw = inner;
      let dh = inner / ratio;
      if (dh > inner) {
        dh = inner;
        dw = inner * ratio;
      }
      ctx.drawImage(img, (size - dw) / 2, (size - dh) / 2, dw, dh);
      resolve(ctx.getImageData(0, 0, size, size));
    }, () => reject(new Error('the mark would not rasterise')));
  });
}

/**
 * Which pixels the mark covers, and how far it reaches.
 *
 * Split from the seeding below, which the site does in one pass, because the
 * loader has to know the mark's proportion *before* it can decide how many
 * motes its box is worth — and this pass is a walk over a quarter of a million
 * pixels, which is not something to do twice for an answer it already has.
 */

/** The Resourced mark at `size`×`size`, as pixels. */
export function rasteriseMark(size = 512) {
  return rasterise(MARK_SVG, size);
}
